import { useEffect, useRef, useState } from 'react';
import { StaffWorkspace } from './SchedulingWorkspace';
import {ReservationsReportWorkspace} from './ReservationsReportWorkspace';
import {SalesReportWorkspace} from './SalesReportWorkspace';
import {PublicBookingWorkspace} from './PublicBookingWorkspace';
import {readPendingGuestBooking} from './PendingGuestBooking';
import {readPendingStoreCart,clearPendingStoreCart} from './PendingStoreCart';
import {readPendingSecretOffer,savePendingSecretOffer,clearPendingSecretOffer} from './PendingSecretOffer';
import {SecretLinkWorkspace} from './SecretLinkWorkspace';
import {PublicStoreWorkspace,PublicGiftCardsWorkspace} from './PublicCommerceWorkspace';
import { LoginScreen,LoginMastheadImage,type AccountAccess } from './LoginScreen';
import { StaffLocationMenu } from './StaffLocationMenu';
import { StaffCommunications } from './StaffCommunications';
import {HelpFeedbackModal} from './HelpFeedbackModal';
import {SupportTasksWorkspace} from './SupportTasksWorkspace';
import { StaffAccountMenu } from './StaffAccountMenu';
import {ChangePasswordPanel} from './ChangePasswordPanel';
import { RoomsWorkspace } from './RoomsWorkspace';
import { GlobalStaffSearch,clearTransientRecentContacts,type StaffSearchCustomer,type StaffSearchTeamMember } from './GlobalStaffSearch';
import { NewCustomerDialog } from './NewCustomerDialog';
import { SalesTicketWorkspace,type SaleDraft,type LinkedAppointmentSale } from './SalesTicketWorkspace';
import {CashboxWorkspace} from './CashboxWorkspace';
import {CustomerMergeWorkspace} from './CustomerMergeWorkspace';
import { TeamMemberProfileWorkspace } from './TeamMemberProfileWorkspace';
import { MeWorkspace } from './MeWorkspace';
import { UserHistoryWorkspace } from './UserHistoryWorkspace';
import { StaffClock } from './StaffClock';
import { StaffHomeWorkspace } from './StaffHomeWorkspace';
import {ProductsWorkspace} from './ProductsWorkspace';
import {DiscountCodesWorkspace} from './DiscountCodesWorkspace';
import {ShopSettingsWorkspace} from './ShopSettingsWorkspace';
import {AutoEmailsWorkspace} from './AutoEmailsWorkspace';
import {ServicesWorkspace} from './ServicesWorkspace';
import {SourceCatalogSetupWorkspace} from './SourceCatalogSetupWorkspace';
import {ReferralProgramsWorkspace} from './ReferralProgramsWorkspace';
import {PublicLocationLogo} from './PublicLocationLogo';
import {WorkforceWorkspace} from './WorkforceWorkspace';
import {bookingPath,locationCodeForBookingId,todayAtLocation} from './BookingLocationRoutes';
import {PublicLocationDirectory} from './PublicLocationDirectory';

type SpaLocation = { code:string;name:string;timezone:string;phone?:string|null;email?:string|null;address?:string|null };
type LocationDirectory = {ok:true;data:{locations:SpaLocation[]}};
type Surface = 'customer-merge'|'cashbox'|'home'|'schedule'|'rooms'|'guest'|'store'|'secret-offer'|'gift-cards'|'login'|'sales'|'team'|'me'|'products'|'discount-codes'|'services'|'packages'|'memberships'|'gift-card-setup'|'waivers'|'shop-settings'|'auto-emails'|'referral-programs'|'shift-schedule'|'my-availability'|'timesheet'|'reservations-report'|'tasks';
const staffNav=['Sales','Schedule','Rooms','Tasks','Logbook','Guide'] as const;
// The public-facing shared sign-in is the first stop on the application
// hostname. A staff or Rooms URL can still be opened directly, but all
// private records continue to require a verified server session.
const routeSurface=():Surface=>(location.pathname.startsWith('/shifts/schedule')||
  location.pathname.startsWith('/shift-schedule'))?'shift-schedule':
  location.pathname.startsWith('/my-availability')?'my-availability':
  location.pathname.startsWith('/timesheet')?'timesheet':
  location.pathname.startsWith('/cashbox')?'cashbox':
  location.pathname.startsWith('/tasks')?'tasks':
  location.pathname.startsWith('/reports/reservations')?'reservations-report':
  location.pathname.startsWith('/customer-merge')?'customer-merge':
  location.pathname.startsWith('/setup/services')?'services':
  location.pathname.startsWith('/setup/packages')?'packages':
  location.pathname.startsWith('/setup/memberships')?'memberships':
  location.pathname.startsWith('/setup/gift-cards')?'gift-card-setup':
  location.pathname.startsWith('/setup/waivers')?'waivers':
  location.pathname.startsWith('/setup/products')?'products':
  location.pathname.startsWith('/setup/discount-codes')?'discount-codes':
  (location.pathname.startsWith('/location-settings')||
    location.pathname.startsWith('/setup/shop-settings'))?'shop-settings':
  location.pathname.startsWith('/setup/auto-emails')?'auto-emails':
  location.pathname.startsWith('/setup/referral-programs')?'referral-programs':
  location.pathname.startsWith('/me')?'me':
  (location.pathname.startsWith('/users/')||location.pathname.startsWith('/team/'))?'team':
  location.pathname.startsWith('/sales')?'sales':
  location.pathname.startsWith('/home')?'home':
  location.pathname.startsWith('/schedule')?'schedule':
  location.pathname.startsWith('/staff')?'home':
  location.pathname.startsWith('/rooms')?'rooms':
  location.pathname.startsWith('/guest')||location.pathname.startsWith('/booking')?'guest':
  location.pathname.startsWith('/shop/secret-links/')?'secret-offer':
  location.pathname.startsWith('/store')?'store':
  location.pathname.startsWith('/gift-cards')?'gift-cards':'login';

/** Resume the guest's latest explicitly initiated checkout or booking action.
 * Browser state only selects navigation; APIs still authorize every write. */
function pendingPublicDestination():{
  surface:'guest'|'store'|'secret-offer';location:string|null;
}|null{
  const booking=readPendingGuestBooking();
  const cart=readPendingStoreCart();
  const secret=readPendingSecretOffer();
  const newest=Math.max(booking?.savedAt??0,cart?.savedAt??0);
  if(secret&&secret.savedAt>=newest)
    return {surface:'secret-offer',location:null};
  return cart&&(!booking||cart.savedAt>=booking.savedAt)
    ?{surface:'store',location:cart.location}
    :booking?{surface:'guest',location:booking.location}:null;
}

export function App() {
  const [locations,setLocations]=useState<SpaLocation[]>([]);
  const [locationStatus,setLocationStatus]=useState<'loading'|'ready'|'unavailable'>('loading');
  const [selectedLocation,setSelectedLocation]=useState<string|null>(()=>
    window.sessionStorage.getItem('ecfs.staff.selectedLocation')||null);
  const loginLocationExplicit=useRef(false);
  const [theme,setTheme]=useState<'light'|'dark'>(()=>
    window.localStorage.getItem('ecfs_theme')==='dark'?'dark':'light');
  const [menuOpen,setMenuOpen]=useState(false);
  const [salesMenuOpen,setSalesMenuOpen]=useState(false);
  const [newCustomerOpen,setNewCustomerOpen]=useState(false);
  const saleSequence=useRef(0);
  const [saleDrafts,setSaleDrafts]=useState<SaleDraft[]>([]);
  const [activeSaleId,setActiveSaleId]=useState<number|null>(null);
  const [saleTicketOpen,setSaleTicketOpen]=useState(false);
  const [helpOpen,setHelpOpen]=useState(false);
  const [focusedSupportTaskId,setFocusedSupportTaskId]=useState<string|null>(null);
  const [salesRefresh,setSalesRefresh]=useState(0);
  const [publicLocationMenuOpen,setPublicLocationMenuOpen]=useState(false);
  const [selectedTeamMember,setSelectedTeamMember]=useState<StaffSearchTeamMember|null>(null);
  const [selectedCustomerProfile,setSelectedCustomerProfile]=useState<StaffSearchCustomer|null>(null);
  const [legacyProfileStatus,setLegacyProfileStatus]=useState('');
  const [profileSelection,setProfileSelection]=useState<{
    customer:StaffSearchCustomer;version:number;locationCode:string;
  }>();
  const [surface,setSurface]=useState<Surface>(routeSurface);
  const [scheduleReload,setScheduleReload]=useState(0);
  const [roomToOpen,setRoomToOpen]=useState<string|null>(null);
  const [access,setAccess]=useState<AccountAccess|null>(null);
  const [authStatus,setAuthStatus]=useState<'checking'|'signed_in'|'signed_out'>('checking');
  const [selfProfile,setSelfProfile]=useState<{actorId:string;displayName:string}|null>(null);
  const [selfAvatarFailed,setSelfAvatarFailed]=useState(false);
  const [selfAvatarRevision,setSelfAvatarRevision]=useState(0);
  const autoStaffEnrollmentStarted=useRef(false);
  const [staffEnrollmentStatus,setStaffEnrollmentStatus]=useState('');

  useEffect(()=>{
    const controller=new AbortController();
    void fetch('/api/v1/locations',{signal:controller.signal,cache:'no-store'})
      .then(async response=>{
        if(!response.ok)throw new Error('Locations unavailable');
        const body=await response.json() as LocationDirectory;
        if(!body.ok||!Array.isArray(body.data.locations))throw new Error('Invalid locations');
        if(controller.signal.aborted)return;
        setLocations(body.data.locations);
        const pending=pendingPublicDestination();
        const routeCode=locationCodeForBookingId(new URLSearchParams(
          window.location.search).get('location'));
        setSelectedLocation(current=>window.location.pathname.startsWith('/booking/')&&
          routeCode&&body.data.locations.some(item=>item.code===routeCode)
          ?routeCode:pending&&body.data.locations.some(item=>item.code===pending.location)
          ?pending.location:body.data.locations.some(item=>item.code===current)
          ?current:(body.data.locations[0]?.code??null));
        setLocationStatus('ready');
      }).catch(()=>{
        if(!controller.signal.aborted)setLocationStatus('unavailable');
      });
    return ()=>controller.abort();
  },[]);

  useEffect(()=>{
    const controller=new AbortController();
    void fetch('/api/v1/auth/access',{credentials:'same-origin',cache:'no-store',
      signal:controller.signal})
      .then(async response=>{
        if(!response.ok){if(!controller.signal.aborted)setAuthStatus('signed_out');return;}
        const body=await response.json() as {ok:boolean;data?:AccountAccess};
        if(controller.signal.aborted)return;
        if(!body.ok||!body.data){setAuthStatus('signed_out');return;}
        setAccess(body.data);
        setAuthStatus('signed_in');
        // Rehydrate the selected authorized location on ALL staff routes,
        // not only /login. A hard refresh must not reset Yardley to Princeton.
        const staff=body.data.staffMode&&body.data.calendarLocations.length>0;
        if(staff){
          const saved=window.sessionStorage.getItem('ecfs.staff.selectedLocation');
          const routeCode=window.location.pathname.startsWith('/booking/')?
            locationCodeForBookingId(new URLSearchParams(window.location.search)
              .get('location')):null;
          setSelectedLocation(routeCode&&body.data.calendarLocations.includes(routeCode)
            ?routeCode:saved&&body.data.calendarLocations.includes(saved)
            ?saved:body.data.defaultLocationCode&&
            body.data.calendarLocations.includes(body.data.defaultLocationCode)
            ?body.data.defaultLocationCode:body.data.calendarLocations[0]);
        }
        // An existing trusted session resumes the current private route.
        if(location.pathname==='/'||location.pathname==='/login'){
          const next=staff?'home':(pendingPublicDestination()?.surface??'me');
          const secret=readPendingSecretOffer();
          window.history.replaceState({},'',staff?'/home':next==='guest'?'/guest':
            next==='store'?'/store':
            next==='secret-offer'&&secret?'/shop/secret-links/'+secret.token:'/me');
          if(next==='secret-offer')clearPendingSecretOffer();
          setSurface(next);
        }
      }).catch(()=>{if(!controller.signal.aborted)setAuthStatus('signed_out');});
    return ()=>controller.abort();
  },[]);

  useEffect(()=>{
    if(authStatus==='signed_in'&&access?.staffMode&&selectedLocation&&
      access.calendarLocations.includes(selectedLocation)){
      window.sessionStorage.setItem('ecfs.staff.selectedLocation',selectedLocation);
    }
  },[authStatus,access,selectedLocation]);

  useEffect(()=>{
    // Legacy /team/:id links retain their person ID but canonicalize the URL.
    if(window.location.pathname.startsWith('/team/')){
      const id=window.location.pathname.split('/')[2]??'';
      window.history.replaceState({},'','/users/'+encodeURIComponent(id));
    }
    if(window.location.pathname==='/staff')window.history.replaceState({},'','/home');
    if(window.location.pathname==='/shift-schedule')
      window.history.replaceState({},'','/shifts/schedule');
  },[]);

  useEffect(()=>{
    if(authStatus!=='signed_in'||!access?.actorKind){setSelfProfile(null);return;}
    const controller=new AbortController();
    void fetch('/api/v1/me',{credentials:'same-origin',cache:'no-store',
      signal:controller.signal}).then(async response=>{
      if(!response.ok)throw Error('PROFILE_UNAVAILABLE');
      const json=await response.json() as {ok:boolean;data?:{
        actorId:string;displayName:string;actorKind:string}};
      if(!controller.signal.aborted&&json.ok&&
        json.data?.actorKind===access.actorKind){
        setSelfProfile({actorId:json.data.actorId,displayName:json.data.displayName});
        setSelfAvatarFailed(false);
      }
    }).catch(()=>{});
    return ()=>controller.abort();
  },[authStatus,access?.actorKind]);

  useEffect(()=>{
    const onAvatarUpdated=(event:Event)=>{
      const changed=(event as CustomEvent<{actorId:string}>).detail?.actorId;
      if(changed&&changed===selfProfile?.actorId){
        setSelfAvatarFailed(false);
        setSelfAvatarRevision(v=>v+1);
      }
    };
    window.addEventListener('ecfs-avatar-updated',onAvatarUpdated);
    return ()=>window.removeEventListener('ecfs-avatar-updated',onAvatarUpdated);
  },[selfProfile?.actorId]);

  // The owner requested ALL current employee accounts be provisioned without
  // manually registering each employee. Use the ordinary owner-authenticated
  // endpoint, never a public or credential-bypassing import endpoint.
  // This runs automatically from the owner's next normal signed-in session.
  useEffect(()=>{
    if(authStatus!=='signed_in'||access?.canManageTeam!==true||
      autoStaffEnrollmentStarted.current)return;
    if(window.sessionStorage.getItem('ecfs_staff_batch_done_20261028')==='yes')return;
    autoStaffEnrollmentStarted.current=true;
    const enroll=async()=>{
      let next:number|null=0;
      let created=0,already=0,conflicts=0,total=0;
      setStaffEnrollmentStatus('Setting up Team Members…');
      try{
        while(next!==null){
          const response=await fetch('/api/v1/staff/team/bulk-enroll',{
            method:'POST',credentials:'same-origin',cache:'no-store',
            headers:{'Content-Type':'application/json'},
            body:JSON.stringify({start:next}),
          });
          const data=await response.json() as {
            ok:boolean;
            data?:{total:number;next:number|null;created:number;already:number;conflicts:number};
          };
          if(!response.ok||!data.ok||!data.data)
            throw new Error('Team Member setup could not be completed.');
          total=data.data.total;
          created+=data.data.created;already+=data.data.already;
          conflicts+=data.data.conflicts;
          next=data.data.next;
        }
        if(conflicts===0&&created+already===total) {
          window.sessionStorage.setItem('ecfs_staff_batch_done_20261028','yes');
          setStaffEnrollmentStatus('All '+total+' Team Members set up.');
        }else{
          setStaffEnrollmentStatus(created+already+' of '+total+
            ' Team Members set up. '+conflicts+' need review.');
        }
      }catch{
        setStaffEnrollmentStatus('Team Member setup incomplete. Retry from the account menu.');
      }
    };
    void enroll();
  },[authStatus,access?.canManageTeam]);

  useEffect(()=>{
    if(surface==='me'){
      if(authStatus==='signed_out'){
        window.history.replaceState({},'','/login');setSurface('login');
      }
      return;
    }
    if(!['home','schedule','rooms','sales','team','shift-schedule',
      'my-availability','timesheet'].includes(surface))return;
    if(authStatus==='signed_out'){
      window.history.replaceState({},'','/login');
      setSurface('login');
    }else if(authStatus==='signed_in'){
      if(!access?.staffMode||access.calendarLocations.length===0){
        window.history.replaceState({},'','/me');
        setSurface('me');
      }else if(!access.calendarLocations.includes(selectedLocation??'')){
        // Never show a location the persisted staff account cannot access.
        setSelectedLocation(access.defaultLocationCode&&
          access.calendarLocations.includes(access.defaultLocationCode)
          ?access.defaultLocationCode:access.calendarLocations[0]);
      }
    }
  },[authStatus,surface,access,selectedLocation]);

  useEffect(()=>{
    const onPop=()=>setSurface(routeSurface());
    window.addEventListener('popstate',onPop);
    return ()=>window.removeEventListener('popstate',onPop);
  },[]);


  // Every person is addressed at /users/ID. New PWA customers use their
  // immutable booking profile ID; employee users use actor ID, or a verified
  // legacy Helm ID when one exists. A direct browser refresh must resolve
  // either person without ever relying on browser-stored contact information.
  useEffect(()=>{
    if(surface!=='team'||!location.pathname.startsWith('/users/')||
      authStatus!=='signed_in'||!access?.staffMode||!selectedLocation)return;
    const userId=decodeURIComponent(location.pathname.split('/')[2]??'');
    if((selectedCustomerProfile?.customerId===userId)||
       (selectedTeamMember&&(selectedTeamMember.helmUserId===userId||
         selectedTeamMember.staffId===userId)))return;
    const controller=new AbortController();
    setLegacyProfileStatus('Loading profile…');
    setSelectedTeamMember(null);setSelectedCustomerProfile(null);
    const get=async(url:string)=>{
      const response=await fetch(url,{credentials:'same-origin',
        cache:'no-store',signal:controller.signal});
      if(!response.ok)return null;
      const body=await response.json() as {ok:boolean;data?:{
        profile?:{customerId:string;actorId?:string|null;actorKind?:string|null;
          displayName:string;homeLocationCode:string|null};
        staffId?:string;displayName?:string;locationCodes?:string[];
        helmUserId?:string|null;
      }};
      return body.ok?body.data??null:null;
    };
    void (async()=>{
      const suffix='?location='+encodeURIComponent(selectedLocation);
      // Non-UUIDs can only be actual verified imported Helm user IDs.
      const uuid=/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
      if(!uuid.test(userId)){
        if(!/^\d{1,19}$/.test(userId))throw Error('Invalid user ID.');
        const legacy=await get('/api/v1/staff/users/'+encodeURIComponent(userId)+
          '/resolve'+suffix);
        if(!legacy?.staffId)throw Error('User profile unavailable.');
        if(!controller.signal.aborted)setSelectedTeamMember(legacy as StaffSearchTeamMember);
      }else{
        const employee=await get('/api/v1/staff/team/'+encodeURIComponent(userId)+
          '/profile'+suffix);
        if(employee?.profile){
          const p=employee.profile;
          if(!controller.signal.aborted)setSelectedTeamMember({
            staffId:userId,displayName:p.displayName,locationCodes:[selectedLocation],
          });
        }else{
          const customer=await get('/api/v1/staff/customers/'+encodeURIComponent(userId)+suffix);
          if(!customer?.profile?.customerId)throw Error('User profile unavailable.');
          const p=customer.profile;
          if(!controller.signal.aborted)setSelectedCustomerProfile({
            customerId:p.customerId,actorId:p.actorId,
            actorKind:p.actorKind==='TEAM_MEMBER'?'TEAM_MEMBER':'CUSTOMER',
            displayName:p.displayName,homeLocationCode:p.homeLocationCode,
          });
        }
      }
      if(!controller.signal.aborted)setLegacyProfileStatus('');
    })().catch(error=>{
      if(!controller.signal.aborted)setLegacyProfileStatus(error instanceof Error?
        error.message:'User profile unavailable.');
    });
    return ()=>controller.abort();
  },[surface,authStatus,access?.staffMode,selectedLocation,
    selectedCustomerProfile?.customerId,selectedTeamMember?.staffId,
    selectedTeamMember?.helmUserId]);

  const canonicalBooking=()=>{
    const code=activeLocation?.code??selectedLocation??'WC';
    const timezone=activeLocation?.timezone??'America/New_York';
    return bookingPath(code,todayAtLocation(timezone));
  };
  const open=(next:Surface)=>{
    const pendingSecretToken=readPendingSecretOffer()?.token;
    // A repeated Schedule click returns from a profile/tool subview to the calendar.
    if(next==='schedule'&&surface==='schedule')setScheduleReload(v=>v+1);
    window.history.pushState({},'',next==='team'?'/users/'+
      (selectedCustomerProfile?.customerId??selectedTeamMember?.helmUserId??
        selectedTeamMember?.staffId):
      next==='home'?'/home':next==='cashbox'?'/cashbox':
      next==='customer-merge'?'/customer-merge':next==='schedule'?'/schedule':
       next==='reservations-report'?'/reports/reservations':
      next==='rooms'?'/rooms':next==='sales'?'/sales':
      next==='shift-schedule'?'/shifts/schedule':
      next==='my-availability'?'/my-availability':
      next==='timesheet'?'/timesheet':
      next==='tasks'?'/tasks':
      next==='products'?'/setup/products':next==='discount-codes'?'/setup/discount-codes':next==='services'?'/setup/services':
      next==='packages'?'/setup/packages':next==='memberships'?'/setup/memberships':
      next==='gift-card-setup'?'/setup/gift-cards':next==='waivers'?'/setup/waivers':next==='shop-settings'?'/location-settings':next==='auto-emails'?'/setup/auto-emails':next==='referral-programs'?'/setup/referral-programs':
      next==='guest'?canonicalBooking():next==='store'?'/store':
      next==='secret-offer'&&pendingSecretToken?'/shop/secret-links/'+pendingSecretToken:
      next==='gift-cards'?'/gift-cards':next==='me'?'/me':'/login');
    setSurface(next);
    setMenuOpen(false);
    setPublicLocationMenuOpen(false);
    setSalesMenuOpen(false);
  };
  const onAuthenticated=(identity:AccountAccess)=>{
    setAccess(identity);
    setAuthStatus('signed_in');
    if(identity.staffMode&&identity.calendarLocations.length){
      const chosen=selectedLocation;
      const code=loginLocationExplicit.current&&chosen&&identity.calendarLocations.includes(chosen)
        ?chosen:identity.defaultLocationCode&&
        identity.calendarLocations.includes(identity.defaultLocationCode)
        ?identity.defaultLocationCode:identity.calendarLocations[0];
      setSelectedLocation(code);
      loginLocationExplicit.current=false;
      open('home');
    }else{
      const pending=pendingPublicDestination();
      if(pending&&locations.some(item=>item.code===pending.location))
        setSelectedLocation(pending.location);
      open(pending?.surface??'me');
      if(pending?.surface==='secret-offer')clearPendingSecretOffer();
    }
  };
  const signOut=async()=>{
    window.dispatchEvent(new Event('ecfs:booking-abandon'));
    const response=await fetch('/api/v1/auth/logout',{
      method:'POST',credentials:'same-origin',cache:'no-store',
    });
    if(!response.ok)throw new Error(response.status===404
      ?'Sign-out is unavailable while staging authentication is disabled.'
      :'Unable to sign out. Please try again.');
    // Recently viewed IDs belong to this staff login, not the next user.
    try{window.sessionStorage.removeItem('ecfs.staff.selectedLocation');}catch{/* no storage */}
    try{window.sessionStorage.removeItem('ecfs.staff.recentProfiles.v1');}catch{/* no storage */}
    clearTransientRecentContacts();
    clearPendingStoreCart();
    clearPendingSecretOffer();
    setAccess(null);
    setAuthStatus('signed_out');
    setProfileSelection(undefined);
    setSelectedTeamMember(null);
    setSaleDrafts([]);setActiveSaleId(null);setSaleTicketOpen(false);
    open('login');
  };
  // Same ordinary sale ticket for either entry point. Header New Sale starts
  // unassigned; customer profile Begin Sale supplies its current customer.
  const beginSaleFor=(customer:{customerId:string;displayName:string}|null)=>{
    const next=++saleSequence.current;
    setSaleDrafts(current=>[...current,{id:next,ticketKey:crypto.randomUUID(),
      locationCode:selectedLocation??'',customerId:customer?.customerId??null,
      customerName:customer?.displayName??null,linkedAppointment:null,
      lines:[],autoItemInitialized:true,preselectedDiscount:null}]);
    setActiveSaleId(next);setSaleTicketOpen(true);setSalesMenuOpen(false);
  };
  const startSale=()=>beginSaleFor(null);
  const openLinkedAppointmentSale=(sale:LinkedAppointmentSale)=>{
    const group=sale.linkedAppointments?.length?sale.linkedAppointments.map(s=>({
      ...s,payerOther:sale.payerOther})): [sale];
    const existing=saleDrafts.find(d=>{
      const prior=d.linkedAppointments?.length?d.linkedAppointments:
        (d.linkedAppointment?[d.linkedAppointment]:[]);
      return prior.some(s=>group.some(g=>g.saleId===s.saleId));
    });
    if(existing){
      setActiveSaleId(existing.id);
      if(sale.preselectedDiscount)setSaleDrafts(current=>current.map(d=>
        d.id===existing.id?{...d,preselectedDiscount:sale.preselectedDiscount}:d));
    }
    else{
      const next=++saleSequence.current;
      setSaleDrafts(current=>[...current,{id:next,ticketKey:sale.saleId,
        locationCode:selectedLocation??'',customerId:sale.payerOther?null:sale.customerId,
        customerName:sale.payerOther?null:sale.customerName,linkedAppointment:sale,
        linkedAppointments:group,lines:[],autoItemInitialized:false,
        preselectedDiscount:sale.preselectedDiscount??null}]);
      setActiveSaleId(next);
    }
    setSaleTicketOpen(true);setSalesMenuOpen(false);
  };
  const cancelSaleDraft=async(draft:SaleDraft)=>{
    if(draft.linkedAppointment){
      const response=await fetch('/api/v1/staff/sales/cancel',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:draft.locationCode,
          saleId:draft.linkedAppointment.saleId,
          ...(draft.linkedAppointments&&draft.linkedAppointments.length>1?
            {saleIds:draft.linkedAppointments.map(item=>item.saleId)}:{})}),
      });
      const body=await response.json() as {ok:boolean;error?:{message?:string;code?:string}};
      if(!response.ok||!body.ok)
        throw new Error(body.error?.message??body.error?.code??'Unable to cancel pending sale.');
    }
    setSaleDrafts(current=>current.filter(item=>item.id!==draft.id));
    setActiveSaleId(null);setSaleTicketOpen(false);
    setSalesRefresh(n=>n+1);
    window.dispatchEvent(new Event('ecfs:refresh-sales-calendar'));
  };
  const openCustomer=(customer:StaffSearchCustomer)=>{
    // Every customer, including a disabled account or a later-hired
    // employee, opens the ONE full-page /users/ID profile.
    // Never navigate back to the historic Schedule-only profile editor.
    setProfileSelection(undefined);
    setSelectedTeamMember(null);
    setSelectedCustomerProfile(customer);
    window.history.pushState({},'','/users/'+encodeURIComponent(customer.customerId));
    setSurface('team');
  };
  const openNewCustomer=(customer:StaffSearchCustomer)=>{
    // Newly created customers use the SAME full-page person workspace as staff.
    setSelectedTeamMember(null);setSelectedCustomerProfile(customer);
    window.history.pushState({},'','/users/'+encodeURIComponent(customer.customerId));
    setSurface('team');
  };
  const openTeamMember=(team:StaffSearchTeamMember)=>{
    setSelectedCustomerProfile(null);setSelectedTeamMember(team);
    window.history.pushState({},'','/users/'+
      encodeURIComponent(team.helmUserId??team.staffId));
    setSurface('team');
  };
  const activeLocation=locations.find(item=>item.code===selectedLocation);
  const locationDisplay=activeLocation?.name??'Select location';
  const locationSales=saleDrafts.filter(d=>d.locationCode===activeLocation?.code);
  const activeSale=locationSales.find(d=>d.id===activeSaleId)??null;
  // Never render staff controls just because someone types a private URL.
  // All underlying private APIs independently enforce persisted permissions.
  // Do not expose a transient staff header with "Select location" before
  // both the authorized session and its actual location directory have loaded.
  // That header can disappear while clicked as asynchronous setup resolves.
  const staffAuthorized=authStatus==='signed_in'&&locationStatus==='ready'&&
    activeLocation!==undefined&&access?.staffMode===true&&
    access.calendarLocations.includes(activeLocation.code);
  const shownSurface=(surface==='home'||surface==='schedule'||surface==='rooms'||surface==='sales'||surface==='team'||surface==='products'||surface==='customer-merge'||surface==='discount-codes'||surface==='services'||surface==='waivers'||surface==='shop-settings'||surface==='auto-emails'||surface==='shift-schedule'||surface==='my-availability'||surface==='timesheet'||surface==='tasks')&&!staffAuthorized
    ?(authStatus==='signed_in'?'me':'login'):surface;


  useEffect(()=>{
    if(!staffAuthorized||!activeLocation)return;
    const controller=new AbortController(),code=activeLocation.code;
    void fetch('/api/v1/staff/sales/open?'+new URLSearchParams({location:code}),
      {credentials:'same-origin',cache:'no-store',signal:controller.signal})
      .then(async response=>{
        if(!response.ok)throw Error('Unable to load pending sales');
        return response.json() as Promise<{ok:boolean;data?:{sales:Array<{
          saleId:string;appointmentId:string;customerId:string;customerName:string;
          serviceName:string;serviceCode:string;locationCode:string;
          preselectedDiscount?:{id:string;code:string;title:string}|null;
          linkedAppointments?:Array<{appointmentId:string;customerId:string;
            customerName:string;serviceName:string;serviceCode:string;saleId:string}>}>}}>;
      })
      .then(body=>{
        if(controller.signal.aborted||!body.ok||!body.data)return;
        setSaleDrafts(current=>{
          const updated=[...current];
          for(const sale of body.data!.sales){
            if(updated.some(d=>d.linkedAppointment?.saleId===sale.saleId))continue;
            updated.push({id:++saleSequence.current,ticketKey:sale.saleId,locationCode:code,
              customerId:sale.customerId,customerName:sale.customerName,
              linkedAppointment:{...sale,payerOther:false},
              linkedAppointments:(sale.linkedAppointments?.length?
                sale.linkedAppointments:[sale]).map(item=>({...item,payerOther:false})),
              lines:[],autoItemInitialized:false,
              preselectedDiscount:sale.preselectedDiscount??null});
          }
          return updated;
        });
      }).catch(()=>{/* Keep visible drafts after a transient read error. */});
    return ()=>controller.abort();
  },[staffAuthorized,activeLocation?.code,salesRefresh]);

  const path=window.location.pathname;
  const historyKind=path.endsWith('/communications')?'communications':
    path.endsWith('/waiver_history')?'waivers':
    path.endsWith('/tasks/completed')?'completed_tasks':null;
  if(historyKind&&surface==='team'){
    const internalStaffId=selectedTeamMember?.staffId??
      (path.startsWith('/team/')?path.split('/')[2]??'':'');
    return <div className="ecfs-app">
      {staffAuthorized&&activeLocation&&internalStaffId
        ?<UserHistoryWorkspace staffId={internalStaffId}
            locationCode={activeLocation.code} kind={historyKind}/>
        :<div role="status" className="ecfs-environment-message">
          {legacyProfileStatus||'Loading user history…'}</div>}
    </div>;
  }

  // Loading is not a signed-out state. Never flash the login form during
  // an existing Team Member session recheck on browser hard-refresh.
  if(authStatus==='checking')return <main className={'app ecfs-app ecfs-theme-'+theme}>
    <p role="status" className="ecfs-environment-message">Restoring your session…</p>
  </main>;
  const isPublicSurface=['guest','store','secret-offer','gift-cards','login','me'].includes(shownSurface);
  // Owner: ALL public pages, including Booking and My Account, use light
  // surfaces independent of OS dark preference and staff settings.
  const publicLight=isPublicSurface;
  return <main className={'app ecfs-app ecfs-theme-'+
    (publicLight?'light':theme)+
    (publicLight?' ecfs-public-shop-light':'')}
    onPointerDownCapture={event=>{
      if(!saleTicketOpen)return;
      const target=event.target as HTMLElement;
      if(target.closest('.ecfs-sales-ticket,.ecfs-sales-menu-anchor,.ecfs-customer-create-backdrop,.ecfs-help-feedback-backdrop,.ecfs-sale-help-backdrop'))return;
      setSaleTicketOpen(false);
    }}>
    {(shownSurface==='tasks'||shownSurface==='cashbox'||shownSurface==='reservations-report'||shownSurface==='home'||shownSurface==='schedule'||shownSurface==='rooms'||shownSurface==='sales'||shownSurface==='team'||shownSurface==='products'||shownSurface==='services'||shownSurface==='packages'||shownSurface==='memberships'||shownSurface==='gift-card-setup'||shownSurface==='waivers'||shownSurface==='shop-settings'||shownSurface==='auto-emails'||shownSurface==='referral-programs'||shownSurface==='shift-schedule'||shownSurface==='my-availability'||shownSurface==='timesheet')
      ?<header className="ecfs-topbar">
        <div className="ecfs-brand-group">
          <div className="ecfs-menu-anchor">
            <button type="button" className="ecfs-location-trigger"
              aria-label={'East Coast Float Spa '+locationDisplay+' location menu'}
              aria-haspopup="menu" aria-expanded={menuOpen}
              onClick={()=>setMenuOpen(current=>!current)}>
              East Coast Float Spa <strong>{locationDisplay}</strong><span aria-hidden="true"> ▾</span>
            </button>
            {menuOpen&&<StaffLocationMenu locations={locations.filter(item=>
              access?.calendarLocations.includes(item.code))}
              selectedLocation={selectedLocation}
              canManageLocationSettings={access?.locationSettingsLocations?.includes(activeLocation?.code??'')===true}
              onSelect={code=>{setSelectedLocation(code);setMenuOpen(false);}}
              onCalendar={()=>open('schedule')} onShifts={()=>open('shift-schedule')}
              onCashbox={()=>open('cashbox')} onMerge={()=>open('customer-merge')}
              onReservationsReport={()=>open('reservations-report')}
              onPublic={()=>open('guest')}
              onOpen={page=>{if(page==='products')open('products');
                else if(page==='services')open('services');
                else if(page==='discount-codes')open('discount-codes');
                else if(page==='packages')open('packages');
                else if(page==='memberships')open('memberships');
                else if(page==='gift-card-setup')open('gift-card-setup');
                else if(page==='waivers')open('waivers');
                else if(page==='shop-settings'&&access?.locationSettingsLocations?.includes(activeLocation?.code??'')===true)open('shop-settings');
                else if(page==='auto-emails')open('auto-emails');
                else if(page==='referral-programs')open('referral-programs');
                else setMenuOpen(false);}}/>}

          </div>
          {activeLocation&&<StaffCommunications locationCode={activeLocation.code}
            onOpenSupportTask={id=>{setFocusedSupportTaskId(id);open('tasks');}}/>}
          {activeLocation&&<GlobalStaffSearch locationCode={activeLocation.code}
            onSelectCustomer={openCustomer} onSelectTeamMember={openTeamMember}/>} 
        </div>
        <nav className="ecfs-global-nav" aria-label="Staff and guest navigation">
          {staffNav.map(section=>section==='Sales'
            ?<div key={section} className="ecfs-sales-menu-anchor">
              <button type="button" className="ecfs-nav-pill ecfs-nav-sales"
                aria-current={shownSurface==='sales'?'page':undefined}
                 onClick={()=>{setSaleTicketOpen(false);open('sales');}}>Sales</button>
              <button type="button" className="ecfs-sales-menu-arrow"
                aria-label="Sales ticket actions" aria-haspopup="menu"
                aria-expanded={salesMenuOpen}
                onClick={()=>setSalesMenuOpen(current=>!current)}>▾</button>
              {locationSales.length>0&&<span className="ecfs-sales-ticket-badge"
                 aria-label={locationSales.length+' open sales'}>{locationSales.length}</span>}
               {salesMenuOpen&&<div className="ecfs-sales-menu" role="menu"
                aria-label="Sales ticket actions">
                <button type="button" role="menuitem" className="ecfs-sales-new-customer"
                  onClick={()=>{setSalesMenuOpen(false);setNewCustomerOpen(true);}}>
                  New Customer
                </button>
                <button type="button" role="menuitem" className="ecfs-sales-new-sale"
                  onClick={startSale}>New Sale</button>
                {locationSales.length>0
                   ?<>
                     <div className="ecfs-open-sale-count">
                       {locationSales.length} Open {locationSales.length===1?'Sale':'Sales'}
                     </div>
                     {locationSales.map(draft=><button type="button" role="menuitem"
                       key={draft.id} className="ecfs-open-sale-entry"
                       onClick={()=>{setActiveSaleId(draft.id);setSaleTicketOpen(true);
                         setSalesMenuOpen(false);}}>
                       <strong>{draft.customerName??'walk-in'}</strong>
                       {draft.linkedAppointment&&<small>
                         1 x {draft.linkedAppointment.serviceName}
                       </small>}
                     </button>)}
                   </>
                   :<div className="ecfs-sales-empty">No open sales.</div>}
              </div>}
            </div>
            :<button type="button" key={section}
            className={'ecfs-nav-pill ecfs-nav-'+section.toLowerCase()}
            aria-current={(shownSurface==='schedule'&&section==='Schedule')||
              (shownSurface==='rooms'&&section==='Rooms')||
              (shownSurface==='tasks'&&section==='Tasks')?'page':undefined}
            disabled={section!=='Schedule'&&section!=='Rooms'&&section!=='Tasks'}
            title={section==='Schedule'?'Appointment calendar':section==='Rooms'?'Room schedules':
              section==='Tasks'?'Task projects':
              section+' will be available when its operational workflow is implemented'}
            onClick={()=>{if(section==='Schedule')open('schedule');
              else if(section==='Rooms')open('rooms');
              else if(section==='Tasks')open('tasks');}}>
            {section} <span aria-hidden="true">▾</span>
          </button>)}
        </nav>
        <div className="ecfs-staff-top-actions">
          <a href="/home" className="ecfs-header-person"
            onClick={event=>{event.preventDefault();open('home');}}>
            {selfProfile?.actorId&&!selfAvatarFailed
              ?<img src={'/api/v1/profiles/'+selfProfile.actorId+'/avatar?v='+selfAvatarRevision}
                alt="" onError={()=>setSelfAvatarFailed(true)}/>
              :<span className="ecfs-header-avatar-fallback" aria-hidden="true">
                {selfProfile?.displayName?.trim().charAt(0)||'•'}
              </span>}
            <span>{selfProfile?.displayName||'My Home'}</span>
          </a>
          {activeLocation&&<StaffClock locationCode={activeLocation.code}/>}
          <StaffAccountMenu onGoCalendar={()=>open('schedule')}
            theme={theme} onThemeChange={next=>{
              setTheme(next);window.localStorage.setItem('ecfs_theme',next);
            }}
            onGoAvailability={()=>open('my-availability')}
            onGoTimesheet={()=>open('timesheet')}
            onGoPublic={()=>open('guest')} onSignOut={signOut}
            canManageTeam={access?.canManageTeam===true}
            automaticBulkStatus={staffEnrollmentStatus}
            onHelpFeedback={()=>setHelpOpen(true)}/>
        </div>
      </header>
      :<header className="ecfs-public-header">
        <div className={'ecfs-public-masthead is-login-masthead'+
          (authStatus==='signed_in'?' has-public-account':'')}>
          <LoginMastheadImage locationCode={activeLocation?.code}/>
          <div className="ecfs-public-identity">{selectedLocation
            ?<PublicLocationLogo locationCode={selectedLocation}/>
            :'East Coast Float Spa'}</div>
          <div className="ecfs-public-location">
            <strong>East Coast Float Spa {locationDisplay}</strong>
            {activeLocation?.address&&<span>{activeLocation.address}</span>}
            {activeLocation?.phone&&<a href={'tel:'+activeLocation.phone}>
              {activeLocation.phone}</a>}
            {activeLocation?.email&&<a href={'mailto:'+activeLocation.email}>
              {activeLocation.email}</a>}
          </div>
          <div className="ecfs-public-location-anchor">
            <button type="button" className="ecfs-public-location-switch"
              aria-expanded={publicLocationMenuOpen}
              aria-label="Choose a Different Location"
              onClick={()=>setPublicLocationMenuOpen(value=>!value)}>
              Choose a Different Location
            </button>
            {publicLocationMenuOpen&&<div className="ecfs-public-location-modal-backdrop"
              onClick={()=>setPublicLocationMenuOpen(false)}>
              <div role="dialog" aria-modal="true" aria-label="Choose a Different Location"
                className="ecfs-public-location-modal" onClick={e=>e.stopPropagation()}>
                <button type="button" aria-label="Close location chooser"
                  onClick={()=>setPublicLocationMenuOpen(false)}>×</button>
                <h2>Choose a Different Location</h2>
                {locations.filter(item=>item.code!==selectedLocation)
                  .map(item=><button type="button" key={item.code}
                    className="ecfs-public-location-option"
                    onClick={()=>{setSelectedLocation(item.code);
                      if(shownSurface==='guest')window.history.pushState({},'',
                        bookingPath(item.code,todayAtLocation(item.timezone)));
                      loginLocationExplicit.current=true;setPublicLocationMenuOpen(false);}}>
                    <strong>East Coast Float Spa {item.name}</strong>
                    {item.address&&<span>{item.address}</span>}
                    {item.phone&&<span>{item.phone}</span>}
                    {item.email&&<span>{item.email}</span>}
                  </button>)}
              </div>
            </div>}
          </div>
          {authStatus==='signed_in'&&<div className="ecfs-public-welcome"
            aria-label="Signed-in account">
            <strong>Welcome, {(selfProfile?.displayName??'Guest').split(' ')[0]}</strong>
            <button type="button" onClick={()=>void signOut()}>Log Out</button>
          </div>}
        </div>
        <nav className="ecfs-public-tabs" aria-label="Public navigation">
          <button type="button" aria-current={shownSurface==='guest'?'page':undefined}
            onClick={()=>open('guest')}>Booking</button>
          <button type="button" aria-current={shownSurface==='store'?'page':undefined}
            onClick={()=>open('store')}>Store</button>
          <button type="button" aria-current={shownSurface==='gift-cards'?'page':undefined}
            onClick={()=>open('gift-cards')}>Gift Cards</button>
          {authStatus==='signed_in'&&<button type="button"
            aria-current={shownSurface==='me'?'page':undefined}
            onClick={()=>open('me')}>My Account</button>}
          {authStatus!=='signed_in'&&
            <button type="button" aria-current={shownSurface==='login'?'page':undefined}
              onClick={()=>{
                if(shownSurface==='secret-offer')
                  savePendingSecretOffer(window.location.pathname.split('/')[3]??'');
                open('login');
              }}>Login</button>}
        </nav>
      </header>}
    {locationStatus==='loading'&&<p className="ecfs-environment-message" role="status">Loading locations…</p>}
    {locationStatus==='unavailable'&&<p className="ecfs-environment-message" role="alert">
      Location directory is temporarily unavailable.
    </p>}
    {locationStatus==='ready'&&locations.length===0&&
      <p className="ecfs-environment-message" role="status">
        No ECFS locations configured in this environment.
      </p>}
    {shownSurface==='login'&&<LoginScreen onAuthenticated={onAuthenticated} locationCode={activeLocation?.code}/>}
    {activeLocation&&(shownSurface==='team'
      ?!selectedTeamMember&&!selectedCustomerProfile
        ?<section role="status" className="ecfs-environment-message">
            {legacyProfileStatus||'Loading profile…'}</section>
        :<TeamMemberProfileWorkspace
          key={selectedCustomerProfile?.customerId??selectedTeamMember?.staffId}
          staffId={selectedTeamMember?.staffId??
            selectedCustomerProfile?.actorId??selectedCustomerProfile?.customerId??''}
          customerId={selectedCustomerProfile?.customerId}
          initial={selectedTeamMember} locationCode={activeLocation.code}
          locationTimezone={activeLocation.timezone}
          viewerActorId={selfProfile?.actorId}
          canManageTeam={access?.canManageTeam===true}
          onBeginSale={beginSaleFor}
          onBack={()=>{setSelectedTeamMember(null);
            setSelectedCustomerProfile(null);open('schedule');}}/>
      :shownSurface==='customer-merge'
      ?<CustomerMergeWorkspace key={activeLocation.code} locationCode={activeLocation.code}/>
      :shownSurface==='reservations-report'
      ?<ReservationsReportWorkspace key={activeLocation.code}
          location={activeLocation}
          locations={locations.filter(item=>access?.calendarLocations.includes(item.code))}
          onChooseLocation={code=>setSelectedLocation(code)}
          onCalendar={()=>open('schedule')}/>
      :shownSurface==='sales'
      ?<SalesReportWorkspace key={activeLocation.code}
          location={activeLocation}
          locations={locations.filter(item=>access?.calendarLocations.includes(item.code))}
          onChooseLocation={code=>setSelectedLocation(code)}
          onCalendar={()=>open('schedule')} onNewSale={startSale}/>
      :shownSurface==='tasks'
      ?<SupportTasksWorkspace key={activeLocation.code}
          locationCode={activeLocation.code} focusTaskId={focusedSupportTaskId}/>
      :shownSurface==='cashbox'
      ?<CashboxWorkspace key={activeLocation.code} locationCode={activeLocation.code}/>
      :shownSurface==='home'
      ?<StaffHomeWorkspace displayName={selfProfile?.displayName??''}
          canApproveVacations={access?.canApproveVacations===true}/>
      :(shownSurface==='packages'||shownSurface==='memberships'||shownSurface==='gift-card-setup')
      ?<SourceCatalogSetupWorkspace locationCode={activeLocation.code}
          category={shownSurface==='packages'?'packages':
            shownSurface==='memberships'?'memberships':'gift_cards'}/>
      :(shownSurface==='services'||shownSurface==='waivers')
      ?<ServicesWorkspace key={activeLocation.code+'-'+shownSurface} initialMode={shownSurface==='waivers'?'waivers':'services'} locationCode={activeLocation.code}
          canEdit={access?.locationSettingsLocations?.includes(activeLocation.code)===true}/>
      :shownSurface==='products'
      ?<ProductsWorkspace key={activeLocation.code} locationCode={activeLocation.code}/>
      :shownSurface==='discount-codes'
      ?<DiscountCodesWorkspace key={activeLocation.code} locationCode={activeLocation.code}/>
      :shownSurface==='shop-settings'
      ?access?.locationSettingsLocations?.includes(activeLocation?.code??'')===true
        ?<ShopSettingsWorkspace key={activeLocation.code} locationCode={activeLocation.code}/>
        :<p role="alert">Location Settings access denied.</p>
      :shownSurface==='referral-programs'
      ?<ReferralProgramsWorkspace key={activeLocation.code} locationCode={activeLocation.code}/>
      :shownSurface==='auto-emails'
      ?<AutoEmailsWorkspace key={activeLocation.code} locationCode={activeLocation.code}/>
      :shownSurface==='shift-schedule'||shownSurface==='my-availability'||
        shownSurface==='timesheet'
      ?<WorkforceWorkspace key={activeLocation.code+'-'+shownSurface}
          location={activeLocation} page={shownSurface}/>
      :shownSurface==='schedule'
      ?<StaffWorkspace key={activeLocation.code+'-'+scheduleReload} location={activeLocation}
          profileSelection={profileSelection} onGoPublic={()=>open('guest')}
          onOpenCancellations={date=>{
            window.history.pushState({},'', '/reports/reservations?'+
              new URLSearchParams({location:activeLocation.code,
                from:date,to:date,status:'cancelled'}));
            setSurface('reservations-report');
          }}
          onOpenPerson={(actorId,displayName)=>openTeamMember({
            staffId:actorId,displayName,locationCodes:[activeLocation.code]})}
          onOpenRoom={resourceId=>{setRoomToOpen(resourceId);open('rooms');}}
          onOpenSale={openLinkedAppointmentSale}
          onBeginCustomerSale={customer=>beginSaleFor(customer)}/>
      :shownSurface==='rooms'
        ?<RoomsWorkspace key={activeLocation.code} location={activeLocation}
          onGoCalendar={()=>open('schedule')} initialRoomId={roomToOpen}
          canReorder={access?.locationSettingsLocations?.includes(activeLocation.code)===true}/>
      :shownSurface==='me'
        ?<><MeWorkspace locationCode={activeLocation.code} onBook={()=>open('guest')}
          onStaff={access?.staffMode?()=>open('schedule'):undefined}/>
          <section className="ecfs-account-password-section" aria-label="My Account password">
            <ChangePasswordPanel/>
          </section></>
      :shownSurface==='guest'
        ?window.location.pathname==='/booking/locations'
          ?<PublicLocationDirectory locations={locations} onChoose={code=>{
              const chosen=locations.find(item=>item.code===code);
              if(!chosen)return;
              setSelectedLocation(code);
              window.history.pushState({},'',bookingPath(code,todayAtLocation(chosen.timezone)));
            }}/>
          :<PublicBookingWorkspace key={activeLocation.code} location={activeLocation}
              isGuestAccount={authStatus==='signed_in'&&access?.actorKind==='CUSTOMER'}
              onBookingAuthenticated={identity=>{
                setAccess(identity);setAuthStatus('signed_in');
                if(identity.actorKind==='TEAM_MEMBER')onAuthenticated(identity);
              }}/>
      :shownSurface==='secret-offer'
        ?<SecretLinkWorkspace
          key={window.location.pathname}
          token={window.location.pathname.split('/')[3]??''}
          isGuestAccount={authStatus==='signed_in'&&
            (access?.actorKind==='CUSTOMER'||access?.actorKind==='TEAM_MEMBER')}
          onLocationResolved={code=>{
            if(locations.some(item=>item.code===code))setSelectedLocation(code);
          }}
          onLogin={()=>{
            const token=window.location.pathname.split('/')[3]??'';
            savePendingSecretOffer(token);
            open('login');
          }}/>
      :shownSurface==='store'
        ?<PublicStoreWorkspace key={activeLocation.code} location={activeLocation}
          isGuestAccount={authStatus==='signed_in'&&
            (access?.actorKind==='CUSTOMER'||access?.actorKind==='TEAM_MEMBER')}
          onLogin={()=>open('login')}/>
      :shownSurface==='gift-cards'
        ?<PublicGiftCardsWorkspace key={activeLocation.code} location={activeLocation}
          isGuestAccount={authStatus==='signed_in'&&
            (access?.actorKind==='CUSTOMER'||access?.actorKind==='TEAM_MEMBER')}
          onLogin={()=>open('login')}/>
        :null)}
    {staffAuthorized&&saleTicketOpen&&activeSale&&
      <SalesTicketWorkspace key={activeSale.id} draft={activeSale}
        onHelpFeedback={()=>setHelpOpen(true)}
        locationName={locations.find(loc=>loc.code===activeSale.locationCode)?.name??activeSale.locationCode}
        onNewCustomer={()=>setNewCustomerOpen(true)}
        onUpdateCustomer={customer=>setSaleDrafts(current=>current.map(d=>d.id===activeSale.id
          ?{...d,customerId:customer?.customerId??null,
            customerName:customer?.displayName??null}:d))}
        onUpdateLines={lines=>setSaleDrafts(current=>current.map(d=>d.id===activeSale.id
          ?{...d,lines}:d))}
        onRestoreGiftLines={giftLines=>setSaleDrafts(current=>current.map(d=>{
          if(d.id!==activeSale.id)return d;
          const keys=new Set(d.lines.map(l=>l.key));
          const missing=giftLines.filter(l=>!keys.has(l.key));
          return missing.length?{...d,lines:[...d.lines,...missing]}:d;
        }))}
        onReconcileGiftBuyer={(reservationIds,buyerId)=>
          setSaleDrafts(current=>current.map(d=>{
            if(d.id!==activeSale.id)return d;
            const keys=new Set(reservationIds);
            return {...d,lines:d.lines.map(l=>
              l.reservedGift&&keys.has(l.reservedGift.reservationId)?
                {...l,reservedGift:{...l.reservedGift,customerId:buyerId}}:l)};
          }))}
        onUpdateManualDiscount={manualDiscount=>setSaleDrafts(current=>
          current.map(d=>d.id===activeSale.id?{...d,manualDiscount}:d))}
        onAutoItemInitialized={lines=>setSaleDrafts(current=>current.map(d=>d.id===activeSale.id
          ?{...d,autoItemInitialized:true,lines:[...d.lines,...lines]}:d))}
        onRemovePreselectedDiscount={async()=>{
          if(!activeSale.linkedAppointment||!activeSale.preselectedDiscount)
            throw Error('No discount selection on an appointment sale ticket.');
          const response=await fetch('/api/v1/staff/sales/'+
            encodeURIComponent(activeSale.linkedAppointment.saleId)+'/discount',{
            method:'DELETE',credentials:'same-origin',
            headers:{'content-type':'application/json'},
            body:JSON.stringify({location:activeSale.locationCode,
              expectedDiscountId:activeSale.preselectedDiscount.id}),
          });
          const body=await response.json() as {ok:boolean;error?:{code?:string}};
          if(!response.ok||!body.ok)
            throw Error(body.error?.code??'Unable to remove selected discount.');
          setSaleDrafts(current=>current.map(d=>d.id===activeSale.id
            ?{...d,preselectedDiscount:null,
              linkedAppointment:d.linkedAppointment?
                {...d.linkedAppointment,preselectedDiscount:null}:null}:d));
        }}
        onComplete={()=>{
          setSaleDrafts(current=>current.filter(d=>d.id!==activeSale.id));
          setActiveSaleId(null);setSaleTicketOpen(false);
          setSalesRefresh(x=>x+1);
          window.dispatchEvent(new Event('ecfs:refresh-sales-calendar'));
        }}
        onMinimize={()=>setSaleTicketOpen(false)}
        onCancelDraft={()=>cancelSaleDraft(activeSale)}/>}
    {helpOpen&&activeLocation&&staffAuthorized&&
      <HelpFeedbackModal locationCode={activeLocation.code}
        onClose={()=>setHelpOpen(false)}/>}
    {newCustomerOpen&&activeLocation&&staffAuthorized&&
      <NewCustomerDialog locationCode={activeLocation.code}
        onClose={()=>setNewCustomerOpen(false)}
        onCreated={customer=>{
          setNewCustomerOpen(false);
          // Sales > New Customer is a standalone customer action.
          // An existing ticket remains OPEN and unchanged, but it must be
          // minimized so the newly created full-page profile is visible.
          setSaleTicketOpen(false);
          openNewCustomer(customer);
        }}/>}
  </main>;
}
